import React, { useState, useEffect } from "react";
import AdminQuizCard from "../../../../cards/AdminQuizCard";
import EmptyState from "../../../../EmptyState";
import AddQuestionModal from "../../../../Modals/AddQuestionModal";
import ConfirmationModal from "../../../../Modals/ConfirmationModal";
import { useTranslation } from "react-i18next";
import { useDeleteLearningTrackQuestionMutation } from "../../../../../services/organization/adminLearningTrack";

const parseAnswers = (answers) => {
  if (typeof answers === "string") {
    try {
      return JSON.parse(answers);
    } catch (e) {
      console.error("Error parsing answers:", e);
      return [];
    }
  }
  return Array.isArray(answers) ? answers : [];
};

function normalizeQuestionForModal(question) {
  if (!question) return null;
  const questionText = question.question ?? question.title ?? "";
  const rawAnswers = Array.isArray(question.answers) ? question.answers : [];
  const answers = rawAnswers.map((a) => ({
    id: a.id,
    answer: a.answer ?? a.title ?? "",
    is_correct: a.is_correct ?? a.isCorrect ?? false,
  }));
  return {
    id: question.id,
    question: questionText,
    answers: answers.length > 0 ? answers : [],
  };
}

const Quizzes = ({ learningTrack }) => {
  const { t } = useTranslation(["common", "learningPaths"]);
  const [addQuestionModalOpen, setAddQuestionModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [deleteQuestion] = useDeleteLearningTrackQuestionMutation();

  const questions = learningTrack?.questions || [];
  const parsedQuestions = questions.map((question) => ({
    ...question,
    answers: parseAnswers(question.answers),
  }));

  const isModalOpen = addQuestionModalOpen || Boolean(editingQuestion);
  const handleCloseModal = () => {
    setAddQuestionModalOpen(false);
    setEditingQuestion(null);
  };
  const handleSuccess = () => {
    setAddQuestionModalOpen(false);
    setEditingQuestion(null);
  };

  useEffect(() => {
    const el = document.getElementById("delete-question-confirmation");
    if (!el) return;
    const bootstrapModal = window.bootstrap?.Modal?.getOrCreateInstance(el);
    if (!bootstrapModal) return;
    if (questionToDelete) {
      bootstrapModal.show();
    } else {
      bootstrapModal.hide();
    }
  }, [questionToDelete]);

  useEffect(() => {
    const el = document.getElementById("delete-question-confirmation");
    if (!el) return;
    const handleHidden = () => setQuestionToDelete(null);
    el.addEventListener("hidden.bs.modal", handleHidden);
    return () => el.removeEventListener("hidden.bs.modal", handleHidden);
  }, []);

  const handleConfirmDelete = async () => {
    if (!questionToDelete?.id || !learningTrack?.id) return;
    try {
      await deleteQuestion({
        learningTrackId: learningTrack.id,
        questionId: questionToDelete.id,
      }).unwrap();
      setQuestionToDelete(null);
    } catch (err) {
      console.error("Failed to delete question:", err);
    }
  };

  return (
    <div id="nav-contact" role="tabpanel" aria-labelledby="nav-contact-tab">
      <div className="container-fluid">
        <div className="row p-4">
          <div className="admin-learning-path-details__quizzes-toolbar d-flex align-center flex-wrap justify-content-between gap-3">
            <div className="admin-learning-path-details__quizzes-heading">
              <h1 className="font_20 font-extrabold">
                {parsedQuestions.length} {t("learningPaths:question")}
              </h1>
              <p className="font_16 font-color-grey mb-0">
                {t("learningPaths:QuestionsAndExamsHint")}
              </p>
            </div>
            {!learningTrack?.archived && (
              <button
                type="button"
                className="btn btn-add-question"
                onClick={() => setAddQuestionModalOpen(true)}
              >
                <i className="fas fa-plus btn-add-question__icon" aria-hidden />
                <span className="btn-add-question__text">
                  {t("learningPaths:addQuestionModalTitle")}
                </span>
              </button>
            )}
          </div>
          <ul className="list-cards mt-4">
            {parsedQuestions.length > 0 ? (
              parsedQuestions.map((question) => (
                <li key={`question-${question.id}`} className="mb-3">
                  <AdminQuizCard
                    quiz={question}
                    onEdit={
                      learningTrack?.archived
                        ? undefined
                        : (q) =>
                            setEditingQuestion(normalizeQuestionForModal(q))
                    }
                    onDelete={
                      learningTrack?.archived
                        ? undefined
                        : (q) => setQuestionToDelete(q)
                    }
                  />
                </li>
              ))
            ) : (
              <EmptyState
                iconComponent={<i className="far fa-question-circle" />}
                title={t("learningPaths:noQuestionsAdded")}
                emptyStateText={t("learningPaths:emptyQuestionsHint")}
              />
            )}
          </ul>
        </div>
      </div>
      <AddQuestionModal
        id="question-form-modal"
        isOpened={isModalOpen}
        onClose={handleCloseModal}
        learningTrackId={learningTrack?.id}
        onSuccess={handleSuccess}
        initialQuestion={editingQuestion}
      />
      <ConfirmationModal
        modalId="delete-question-confirmation"
        isOpened={Boolean(questionToDelete)}
        onClose={() => setQuestionToDelete(null)}
        confirmationMessage={t("learningPaths:confirmDeleteQuestion")}
        handleConfirmation={handleConfirmDelete}
      />
    </div>
  );
};

export default Quizzes;
